class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    // Normalize the shop money format to plain text: decode HTML entities
    // (&euro; etc.) and strip tags (<span class=money>), since the formatted
    // value is inserted via both textContent and escaped HTML templates.
    const rawFormat = this.drawer.dataset.moneyFormat || '';
    this.moneyFormat = new DOMParser().parseFromString(rawFormat, 'text/html').documentElement.textContent || '';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.changeQueue = Promise.resolve();
    this.targetQuantities = new Map();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';

    this.shippingBar = this.drawer.querySelector('[data-cart-shipping-bar]');
    this.shippingMessage = this.drawer.querySelector('[data-cart-shipping-message]');
    this.shippingTrack = this.drawer.querySelector('[data-cart-shipping-track]');
    this.shippingFill = this.drawer.querySelector('[data-cart-shipping-fill]');
    this.shippingThreshold = Number(this.drawer.dataset.cartShippingThreshold || 0);
    this.shippingRemainingTemplate = this.drawer.dataset.cartShippingRemainingTemplate || '';
    this.shippingReachedText = this.drawer.dataset.cartShippingReachedText || '';

    this.progressMode = this.drawer.dataset.cartProgressMode || 'shipping';
    this.milestones = [];
    try {
      this.milestones = JSON.parse(this.drawer.dataset.cartMilestones || '[]') || [];
    } catch (parseError) {
      this.milestones = [];
    }
    this.milestones.sort((a, b) => a.q - b.q);
    this.milestoneRemainingTemplate = this.drawer.dataset.cartMilestoneRemainingTemplate || '';
    this.milestoneReachedTemplate = this.drawer.dataset.cartMilestoneReachedTemplate || '';

    this.youSaveTemplate = this.drawer.dataset.cartYouSaveTemplate || '';
    this.showSavingsChip = this.drawer.dataset.cartShowSavingsChip === 'true';
    this.showVariantSelectors = this.drawer.dataset.cartShowVariantSelectors === 'true';
    this.productCache = new Map();
    this.discountsRow = this.drawer.querySelector('[data-cart-discounts-row]');
    this.discountsValue = this.drawer.querySelector('[data-cart-discounts-value]');

    this.upsellsContainer = this.drawer.querySelector('[data-cart-upsells]');

    this.addonToggle = this.drawer.querySelector('[data-cart-addon-toggle]');
    this.addonVariantId = Number(this.drawer.dataset.cartAddonVariant || 0);

    this.giftVariantId = Number(this.drawer.dataset.cartGiftVariant || 0);
    this.giftThreshold = Number(this.drawer.dataset.cartGiftThreshold || 0);
    this.giftRemainingTemplate = this.drawer.dataset.cartGiftRemainingTemplate || '';
    this.giftUnlockedText = this.drawer.dataset.cartGiftUnlockedText || '';
    this.giftLabel = this.drawer.dataset.cartGiftLabel || '';
    this.giftStatusNode = this.drawer.querySelector('[data-cart-gift-status]');
    this.giftBusy = false;
    this.giftDismissKey = 'yc-cart-gift-dismissed';
    this.lastCart = null;

    this.timerNode = this.drawer.querySelector('[data-cart-timer]');
    this.timerMinutes = Number(this.drawer.dataset.cartTimerMinutes || 10);
    this.timerTemplate = this.drawer.dataset.cartTimerTemplate || '';
    this.timerInterval = null;

    this.bindUI();
  }

  // When the UpCart app is installed it intercepts add-to-cart and opens its
  // own drawer. If the theme drawer also opens, the customer sees two carts.
  // Detect UpCart (checked lazily, since its script loads asynchronously) and
  // let it own the cart in that case.
  isUpCartActive() {
    // Only treat UpCart as active when its runtime API is actually present —
    // matching on id/class/script substrings gives false positives from
    // leftover embed containers, which would leave the cart icon fully dead.
    return Boolean(
      window.UpCart ||
        (window.upcartOpenCart && typeof window.upcartOpenCart === 'function')
    );
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        // Always stop the native navigation to /cart. When UpCart is active,
        // let its own click handler open its drawer instead of the theme one.
        event.preventDefault();
        if (this.isUpCartActive()) return;
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));

    // Quick-add buttons (product tiles) add via fetch outside a form and
    // announce themselves with this event so the drawer refreshes and opens.
    document.addEventListener('yc:cart:added', async () => {
      await this.refresh();
      if (!this.isUpCartActive()) this.open();
    });
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    // Restore focus to the add button when the drawer later closes —
    // otherwise close() leaves focus stranded in the hidden drawer subtree.
    this.lastActiveElement = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        // Shopify returns JSON with a human-readable message (e.g. inventory
        // limits) — surface it instead of navigating away and losing it.
        let message = 'Unable to add item to cart';
        try {
          const body = await response.json();
          message = body.description || body.message || message;
        } catch (parseError) {
          // Non-JSON error body — keep the generic message.
        }
        throw new Error(message, { cause: 'cart-add' });
      }

      await response.json();
      // When UpCart is active it intercepts the cart/add.js fetch above and
      // opens its own drawer. Don't open the theme drawer too, or both show.
      if (this.isUpCartActive()) {
        await this.refresh();
      } else {
        await this.refresh();
        this.open();
      }
    } catch (error) {
      console.error(error);
      if (error.cause === 'cart-add') {
        this.showAddToCartError(form, error.message);
      } else {
        // Network/unexpected failure — fall back to the classic cart page.
        window.location.href = form.action || '/cart';
      }
    } finally {
      this.setAddButtonState(false);
    }
  }

  showAddToCartError(form, message) {
    let errorNode = form.querySelector('[data-cart-add-error]');
    if (!errorNode) {
      errorNode = document.createElement('p');
      errorNode.setAttribute('data-cart-add-error', '');
      errorNode.setAttribute('role', 'alert');
      errorNode.style.cssText = 'color:#b3261e;font-size:0.85rem;margin:8px 0 0;';
      const submitButton = form.querySelector('[name="add"]');
      (submitButton?.parentElement || form).appendChild(errorNode);
    }
    errorNode.textContent = message;
    if (this.liveRegion) this.liveRegion.textContent = message;
    clearTimeout(this.addErrorTimer);
    this.addErrorTimer = setTimeout(() => {
      errorNode.textContent = '';
    }, 6000);
  }

  async handleDrawerClick(event) {
    const upsellTrigger = event.target.closest('[data-cart-upsell-add]');
    if (upsellTrigger && upsellTrigger.tagName === 'BUTTON') {
      this.addUpsell(upsellTrigger);
      return;
    }

    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      // Base rapid clicks on the already-queued target so "+ + +" yields +3
      // (previously clicks during an in-flight request were silently dropped).
      const baseQuantity = this.targetQuantities.has(key)
        ? this.targetQuantities.get(key)
        : Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, baseQuantity + delta);

      this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key) return;

      this.markGiftDismissedIfGift(key);
      this.changeItem(key, 0);
    }
  }

  async handleDrawerChange(event) {
    const addonToggle = event.target.closest('[data-cart-addon-toggle]');
    if (addonToggle) {
      this.handleAddonToggle(addonToggle);
      return;
    }

    const optionSelect = event.target.closest('[data-cart-option-select]');
    if (optionSelect) {
      this.handleOptionChange(optionSelect);
      return;
    }

    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key) return;

    const quantity = Math.max(0, Number(input.value) || 0);
    if (quantity === 0) this.markGiftDismissedIfGift(key);
    this.changeItem(key, quantity);
  }

  changeItem(key, quantity) {
    if (!key) return Promise.resolve();

    // Coalesce: remember only the latest target per key. All requests run
    // through one serialized queue, so responses can never render out of
    // order (parallel updates on two items used to race — last response won,
    // occasionally showing a cart snapshot missing the other item's change).
    this.targetQuantities.set(key, quantity);
    this.setItemPendingState(key, true);

    this.changeQueue = this.changeQueue.then(async () => {
      if (!this.targetQuantities.has(key)) return; // a later queued op already sent it
      const target = this.targetQuantities.get(key);
      this.targetQuantities.delete(key);

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: target
          })
        });

        if (!response.ok) {
          throw new Error('Unable to update cart item');
        }

        const cart = await response.json();
        this.render(cart);
      } catch (error) {
        console.error(error);
        await this.refresh();
      } finally {
        this.setItemPendingState(key, false);
      }
    });

    return this.changeQueue;
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;

    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (currentCount > 0) {
      this.showSkeleton(Math.min(currentCount, 3));
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });
      const cart = await response.json();
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    const itemCount = cart.item_count || 0;

    if (this.drawerCount) {
      this.drawerCount.textContent = itemCount;
    }

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    // Kaching-style summary: all perceived savings (compare-at + real
    // discounts) roll up into one "Discounts" row; no struck subtotal.
    const compareTotal = (cart.items || []).reduce(
      (sum, item) => sum + (item.compare_at_line_price || item.original_line_price || item.final_line_price || 0),
      0
    );
    if (this.compareSubtotalNode) {
      this.compareSubtotalNode.hidden = true;
      this.compareSubtotalNode.textContent = '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }

    if (this.discountsRow) {
      const savingsTotal = Math.max(0, compareTotal - (cart.total_price || 0));
      this.discountsRow.hidden = savingsTotal <= 0;
      if (this.discountsValue) {
        this.discountsValue.textContent = `-${this.formatMoney(savingsTotal)}`;
      }
    }

    this.lastCart = cart;
    this.updateShippingBar(cart);
    this.updateUpsells(cart);
    this.updateTimer(itemCount);
    this.updateAddon(cart);
    this.updateGift(cart);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
          <a href="${this.escapeHtml(this.continueUrl)}" class="button button--primary cart-drawer__empty-cta">
            ${this.escapeHtml(this.continueLabel)}
          </a>
        </div>
      `;
      return;
    }

    const itemsMarkup = cart.items
      // The add-on is represented by the footer toggle, not a line item.
      .filter((item) => !(this.addonToggle && Number(item.variant_id) === this.addonVariantId))
      .map((item) => {
        // Request a sized variant instead of the full-size original — the
        // thumb renders at ~96px, so the raw CDN image is pure over-download.
        const sizedImage = item.image
          ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=320`
          : '';
        const imageMarkup = item.image
          ? `<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">
              <img src="${this.escapeHtml(sizedImage)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy">
            </a>`
          : `<div class="cart-drawer__item-media"></div>`;

        const giftBadgeMarkup =
          this.giftVariantId && Number(item.variant_id) === this.giftVariantId
            ? `<p class="cart-drawer__item-gift-badge">${this.escapeHtml(this.giftLabel)}</p>`
            : '';

        const sellingPlanName = item.selling_plan_allocation?.selling_plan?.name;
        const sellingPlanMarkup = sellingPlanName
          ? `<p class="cart-drawer__item-variant">${this.escapeHtml(sellingPlanName)}</p>`
          : '';

        // Selects render with only the current values; hydrateOptionSelects()
        // fills in the full option lists from the cached /products/*.js data.
        const hasRealOptions = !item.product_has_only_default_variant;
        const useSelectors = this.showVariantSelectors && hasRealOptions && !sellingPlanName;
        let optionsMarkup = '';
        if (useSelectors) {
          const names = (item.options_with_values || []).map((option) => option.name).join(', ');
          const selects = (item.options_with_values || [])
            .map(
              (option, index) => `
                <select class="cart-drawer__item-option-select" data-cart-option-select data-option-position="${index + 1}" aria-label="${this.escapeHtml(option.name)}">
                  <option value="${this.escapeHtml(option.value)}" selected>${this.escapeHtml(option.value)}</option>
                </select>`
            )
            .join('');
          optionsMarkup = `
            <p class="cart-drawer__item-options-label">${this.escapeHtml(names)}:</p>
            <div class="cart-drawer__item-options" data-cart-item-options>${selects}</div>`;
        } else if (item.variant_title && item.variant_title !== 'Default Title') {
          optionsMarkup = `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`;
        }

        const compareLine =
          this.showCompareAt && (item.compare_at_line_price || 0) > (item.final_line_price || 0)
            ? item.compare_at_line_price
            : 0;
        const comparePriceMarkup = compareLine
          ? `<s class="cart-drawer__item-compare-price">${this.formatMoney(compareLine)}</s>`
          : '';
        const savings = compareLine ? compareLine - (item.final_line_price || 0) : 0;
        const savingsMarkup =
          this.showSavingsChip && savings > 0
            ? `<span class="cart-drawer__item-savings">${this.youSaveTemplate.replace('[[amount]]', this.formatMoney(savings))}</span>`
            : '';

        const isPending = this.targetQuantities.has(item.key);

        return `
          <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${item.key}" data-cart-item-handle="${this.escapeHtml(item.handle)}" data-cart-item-variant="${item.variant_id}" aria-busy="${isPending ? 'true' : 'false'}">
            ${imageMarkup}
            <div class="cart-drawer__item-content">
              <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
              ${giftBadgeMarkup}
              ${sellingPlanMarkup}
              ${optionsMarkup}
              <div class="cart-drawer__quantity">
                <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>&minus;</button>
                <input type="number" class="cart-drawer__quantity-value" value="${item.quantity}" min="1" aria-label="${this.escapeHtml(this.quantityLabel)}" data-cart-quantity-input ${isPending ? 'disabled' : ''}>
                <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>+</button>
              </div>
            </div>
            <div class="cart-drawer__item-side">
              <div class="cart-drawer__item-prices">
                ${comparePriceMarkup}
                <p class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>
                ${savingsMarkup}
              </div>
              <button type="button" class="cart-drawer__remove" data-cart-remove ${isPending ? 'disabled' : ''} aria-label="${this.escapeHtml(this.removeLabel)}: ${this.escapeHtml(item.product_title)}">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </li>
        `;
      })
      .join('');

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>`;
    this.hydrateOptionSelects();
  }

  async fetchProductData(handle) {
    if (!handle) return null;
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    this.productCache.set(handle, request);
    return request;
  }

  async hydrateOptionSelects() {
    if (!this.showVariantSelectors || !this.body) return;

    const items = [...this.body.querySelectorAll('[data-cart-item-key][data-cart-item-handle]')];
    await Promise.all(
      items.map(async (itemNode) => {
        const selects = [...itemNode.querySelectorAll('[data-cart-option-select]')];
        if (!selects.length) return;

        const product = await this.fetchProductData(itemNode.dataset.cartItemHandle);
        if (!product?.variants?.length) return;
        // The item may have been re-rendered while fetching — re-query.
        if (!itemNode.isConnected) return;

        selects.forEach((select) => {
          const position = Number(select.dataset.optionPosition);
          const current = select.value;
          const values = [];
          product.variants.forEach((variant) => {
            const value = variant[`option${position}`];
            if (value != null && !values.includes(value)) values.push(value);
          });
          if (!values.length) return;

          select.innerHTML = values
            .map(
              (value) =>
                `<option value="${this.escapeHtml(value)}" ${value === current ? 'selected' : ''}>${this.escapeHtml(value)}</option>`
            )
            .join('');
        });
      })
    );
  }

  async handleOptionChange(select) {
    const itemNode = select.closest('[data-cart-item-key]');
    if (!itemNode) return;

    const key = itemNode.dataset.cartItemKey;
    const currentVariantId = Number(itemNode.dataset.cartItemVariant);
    const quantityInput = itemNode.querySelector('[data-cart-quantity-input]');
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);

    const product = await this.fetchProductData(itemNode.dataset.cartItemHandle);
    if (!product?.variants?.length) return;

    const selects = [...itemNode.querySelectorAll('[data-cart-option-select]')];
    const chosen = {};
    selects.forEach((node) => {
      chosen[Number(node.dataset.optionPosition)] = node.value;
    });

    const variant = product.variants.find((candidate) =>
      Object.entries(chosen).every(([position, value]) => candidate[`option${position}`] === value)
    );

    if (!variant || Number(variant.id) === currentVariantId) {
      // No matching variant for this combination — revert the selects to the
      // line's actual variant options.
      if (!variant) this.refresh();
      return;
    }

    this.setItemPendingState(key, true);
    // Serialized through the same queue as quantity changes so a rapid
    // quantity tap can't interleave with the remove+add pair.
    this.changeQueue = this.changeQueue.then(async () => {
      try {
        const changeResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!changeResponse.ok) throw new Error('Unable to update cart item');
        await this.addVariantToCart(variant.id, quantity);
      } catch (error) {
        console.error(error);
        if (this.liveRegion) this.liveRegion.textContent = error.message;
      } finally {
        await this.refresh();
      }
    });
    return this.changeQueue;
  }

  updateShippingBar(cart) {
    if (!this.shippingBar) return;

    const itemCount = cart.item_count || 0;
    this.shippingBar.hidden = itemCount === 0;
    if (itemCount === 0) return;

    if (this.progressMode === 'milestones' && this.milestones.length) {
      // The shipping-protection add-on and the auto free gift don't count
      // toward discount milestones — a "buy 3 items" discount won't count
      // them either.
      const qualifyingCount = (cart.items || []).reduce((sum, item) => {
        const variantId = Number(item.variant_id);
        if (variantId === this.addonVariantId || variantId === this.giftVariantId) return sum;
        return sum + (item.quantity || 0);
      }, 0);
      // Dots sit at even intervals (Kaching-style), so the fill is piecewise:
      // fully-passed segments plus the fraction into the current one.
      const n = this.milestones.length;
      const reached = this.milestones.filter((milestone) => milestone.q <= qualifyingCount).length;
      let pct;
      if (reached >= n) {
        pct = 100;
      } else {
        const prevQ = reached > 0 ? this.milestones[reached - 1].q : 0;
        const nextQ = this.milestones[reached].q;
        const fraction = Math.max(0, Math.min(1, (qualifyingCount - prevQ) / (nextQ - prevQ)));
        pct = Math.round(((reached + fraction) / n) * 100);
      }
      const next = this.milestones.find((milestone) => milestone.q > qualifyingCount);
      const last = this.milestones[this.milestones.length - 1];

      if (this.shippingFill) this.shippingFill.style.width = `${pct}%`;
      if (this.shippingTrack) this.shippingTrack.setAttribute('aria-valuenow', String(pct));

      this.drawer.querySelectorAll('[data-cart-milestone-dot], [data-cart-milestone-label]').forEach((node) => {
        node.classList.toggle('is-reached', qualifyingCount >= Number(node.dataset.milestoneQuantity));
      });

      if (this.shippingMessage) {
        this.shippingMessage.classList.toggle('is-reached', !next);
        this.shippingMessage.innerHTML = next
          ? this.milestoneRemainingTemplate
              .replace('[[count]]', `<strong>${next.q - qualifyingCount}</strong>`)
              .replace('[[reward]]', `<strong>${this.escapeHtml(next.label)}</strong>`)
          : this.milestoneReachedTemplate.replace('[[reward]]', `<strong>${this.escapeHtml(last.label)}</strong>`);
      }
      return;
    }

    if (!this.shippingThreshold) return;

    // The threshold setting is in the store's main currency; convert it to the
    // active presentment currency so Markets shoppers see correct progress.
    const rate = Number(window.Shopify?.currency?.rate || 1) || 1;
    const threshold = this.shippingThreshold * rate;
    const total = cart.total_price || 0;
    const remaining = Math.max(0, threshold - total);
    const pct = Math.min(100, Math.round((total / threshold) * 100));
    const reached = remaining === 0;

    if (this.shippingFill) {
      this.shippingFill.style.width = `${pct}%`;
      this.shippingFill.classList.toggle('is-reached', reached);
    }
    if (this.shippingTrack) {
      this.shippingTrack.setAttribute('aria-valuenow', String(pct));
    }
    if (this.shippingMessage) {
      this.shippingMessage.classList.toggle('is-reached', reached);
      this.shippingMessage.textContent = reached
        ? this.shippingReachedText
        : this.shippingRemainingTemplate.replace('[[amount]]', this.formatMoney(remaining));
    }
  }

  updateUpsells(cart) {
    if (!this.upsellsContainer) return;

    const itemCount = cart.item_count || 0;
    const cartProductIds = new Set((cart.items || []).map((item) => Number(item.product_id)));
    const cards = [...this.upsellsContainer.querySelectorAll('[data-cart-upsell]')];

    let visibleCount = 0;
    cards.forEach((card) => {
      const inCart = cartProductIds.has(Number(card.dataset.productId));
      card.hidden = inCart;
      if (!inCart) visibleCount += 1;
    });

    this.upsellsContainer.hidden = itemCount === 0 || visibleCount === 0;
  }

  async addUpsell(button) {
    const variantId = Number(button.dataset.variantId || 0);
    if (!variantId || button.disabled) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = this.addingLabel || originalLabel;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) {
        let message = 'Unable to add item to cart';
        try {
          const body = await response.json();
          message = body.description || body.message || message;
        } catch (parseError) {
          // Non-JSON error body — keep the generic message.
        }
        throw new Error(message);
      }

      await response.json();
      await this.refresh();
    } catch (error) {
      console.error(error);
      if (this.liveRegion) this.liveRegion.textContent = error.message;
    } finally {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }

  findCartItemByVariant(cart, variantId) {
    return (cart?.items || []).find((item) => Number(item.variant_id) === Number(variantId));
  }

  async addVariantToCart(variantId, quantity = 1) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: variantId, quantity })
    });

    if (!response.ok) {
      let message = 'Unable to add item to cart';
      try {
        const body = await response.json();
        message = body.description || body.message || message;
      } catch (parseError) {
        // Non-JSON error body — keep the generic message.
      }
      throw new Error(message);
    }

    return response.json();
  }

  async handleAddonToggle(toggle) {
    if (!this.addonVariantId) return;

    toggle.disabled = true;
    try {
      if (toggle.checked) {
        await this.addVariantToCart(this.addonVariantId);
        await this.refresh();
      } else {
        const line = this.findCartItemByVariant(this.lastCart, this.addonVariantId);
        if (line) await this.changeItem(line.key, 0);
      }
    } catch (error) {
      console.error(error);
      toggle.checked = !toggle.checked;
      if (this.liveRegion) this.liveRegion.textContent = error.message;
    } finally {
      toggle.disabled = false;
    }
  }

  updateAddon(cart) {
    if (!this.addonToggle || !this.addonVariantId) return;
    // Sync, don't fire: setting .checked programmatically doesn't emit a
    // change event, so this can't loop back into handleAddonToggle.
    this.addonToggle.checked = Boolean(this.findCartItemByVariant(cart, this.addonVariantId));
  }

  isGiftDismissed() {
    try {
      return sessionStorage.getItem(this.giftDismissKey) === 'true';
    } catch (storageError) {
      return false;
    }
  }

  markGiftDismissedIfGift(key) {
    if (!this.giftVariantId) return;
    const line = (this.lastCart?.items || []).find((item) => item.key === key);
    if (!line || Number(line.variant_id) !== this.giftVariantId) return;
    try {
      sessionStorage.setItem(this.giftDismissKey, 'true');
    } catch (storageError) {
      // Storage blocked — the gift may re-add on the next threshold check.
    }
  }

  async updateGift(cart) {
    if (!this.giftVariantId || !this.giftThreshold) return;

    const itemCount = cart.item_count || 0;
    const giftLine = this.findCartItemByVariant(cart, this.giftVariantId);

    // The gift's own line never counts toward the threshold — otherwise a
    // not-yet-discounted gift price could push the total across its own
    // threshold and the add/remove logic would oscillate.
    const baseTotal = (cart.total_price || 0) - (giftLine?.final_line_price || 0);
    const rate = Number(window.Shopify?.currency?.rate || 1) || 1;
    const threshold = this.giftThreshold * rate;
    const remaining = Math.max(0, threshold - baseTotal);
    const qualified = itemCount > 0 && remaining === 0;

    if (this.giftStatusNode) {
      this.giftStatusNode.hidden = itemCount === 0;
      if (itemCount > 0) {
        this.giftStatusNode.classList.toggle('is-unlocked', qualified);
        this.giftStatusNode.textContent = qualified
          ? this.giftUnlockedText
          : this.giftRemainingTemplate.replace('[[amount]]', this.formatMoney(remaining));
      }
    }

    if (this.giftBusy) return;

    if (qualified && !giftLine && !this.isGiftDismissed()) {
      this.giftBusy = true;
      try {
        await this.addVariantToCart(this.giftVariantId);
        await this.refresh();
      } catch (error) {
        // Gift may be sold out — log and keep the drawer functional.
        console.error(error);
      } finally {
        this.giftBusy = false;
      }
    } else if (!qualified && giftLine) {
      this.giftBusy = true;
      try {
        await this.changeItem(giftLine.key, 0);
      } finally {
        this.giftBusy = false;
      }
    }
  }

  updateTimer(itemCount) {
    if (!this.timerNode) return;

    if (!itemCount || !this.timerTemplate) {
      this.timerNode.hidden = true;
      this.stopTimer();
      return;
    }

    this.timerNode.hidden = false;
    if (this.timerInterval) return;

    const tick = () => {
      let deadline = 0;
      try {
        deadline = Number(sessionStorage.getItem('yc-cart-timer-deadline') || 0);
      } catch (storageError) {
        deadline = this.memoryDeadline || 0;
      }
      const now = Date.now();
      // Evergreen: (re)start the countdown when absent or expired.
      if (!deadline || deadline <= now) {
        deadline = now + this.timerMinutes * 60 * 1000;
        try {
          sessionStorage.setItem('yc-cart-timer-deadline', String(deadline));
        } catch (storageError) {
          // Private browsing may block sessionStorage — timer still ticks in-memory.
          this.memoryDeadline = this.memoryDeadline || deadline;
          deadline = this.memoryDeadline;
        }
      }
      const remaining = Math.max(0, deadline - now);
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      const formatted = `${minutes}:${String(seconds).padStart(2, '0')}`;
      this.timerNode.textContent = this.timerTemplate.replace('[[time]]', formatted);
    };

    tick();
    this.timerInterval = setInterval(tick, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    const amount = (cents || 0) / 100;

    // Prefer the shop's configured money format so drawer prices match
    // Liquid-rendered prices exactly (separators, symbol placement, etc.).
    const placeholder = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (placeholder) {
      const group = (integer, separator) => integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      let value;
      switch (placeholder[1]) {
        case 'amount_no_decimals':
          value = group(Math.round(amount).toString(), ',');
          break;
        case 'amount_with_comma_separator':
          value = group(amount.toFixed(2).replace('.', ','), '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = group(Math.round(amount).toString(), '.');
          break;
        case 'amount_with_apostrophe_separator':
          value = group(amount.toFixed(2), "'");
          break;
        case 'amount_with_space_separator':
          value = group(amount.toFixed(2).replace('.', ','), ' ');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = group(Math.round(amount).toString(), ' ');
          break;
        case 'amount_with_period_and_space_separator':
          value = group(amount.toFixed(2), ' ');
          break;
        case 'amount':
        default:
          value = group(amount.toFixed(2), ',');
          break;
      }
      return this.moneyFormat.replace(placeholder[0], value);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format(amount);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!this.showCompareAt || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerController();
});